import { NgTemplateOutlet } from '@angular/common';
import {
  Component, DestroyRef, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, computed, effect, inject,
  signal, untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, Subscription, catchError, debounceTime, map, of, switchMap } from 'rxjs';

import { EnvioMensagemTemplate } from '../../../shared/model/chat/mensagens/envio-mensagem-template.model';
import { TemplateBusca } from '../../../shared/model/template/template-busca.model';
import { TemplateChatResumo } from '../../../shared/model/template/template-chat-resumo.model';
import { AuthService } from '../../../shared/service/auth.service';
import { EnvioTemplateChatService } from '../../../shared/service/envio-template-chat.service';
import { MsgService } from '../../../shared/service/msg.service';
import { TemplatesChatService } from '../../../shared/service/templates-chat.service';
import { TemplatesChatPagina } from '../../../shared/model/template/templates-chat-pagina.model';
import { ESPERA_BUSCA_TEMPLATES_MS, TELAS_PRE_CARGA_TEMPLATES } from '../../../shared/util/templates-chat.constantes';
import { ArrastarFecharSheetDirective } from '../../utils/arrastar-fechar-sheet.directive';
import { PedidoPaginaTemplates } from './models/pedido-pagina-templates.model';
import { SegmentoPrevia } from './models/segmento-previa.model';
import { VariavelSelecao } from './models/variavel-selecao.model';
import { LARGURA_MINIMA_DUAS_COLUNAS, LINHAS_SKELETON, ROTULO_CAMPO_FIXO } from './utils/selecao-template.constantes';

@Component({
  selector: 'app-selecao-template',
  standalone: true,
  imports: [NgTemplateOutlet, ArrastarFecharSheetDirective],
  templateUrl: './selecao-template.component.html',
  styleUrl: './selecao-template.component.css'
})
export class SelecaoTemplateComponent implements OnInit, OnDestroy {
  protected readonly linhasSkeleton = LINHAS_SKELETON;
  protected readonly horaAtual = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  @Input() numeroContato = '';
  @Input() nomeContato = '';

  @Output() fechar = new EventEmitter<void>();
  @Output() enviado = new EventEmitter<EnvioMensagemTemplate>();

  private readonly templatesChat = inject(TemplatesChatService);
  private readonly envioTemplateChatService = inject(EnvioTemplateChatService);
  private readonly msgService = inject(MsgService);
  private readonly authService = inject(AuthService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('listaTemplates') listaTemplates?: ElementRef<HTMLElement>;

  readonly templates = signal<TemplateChatResumo[]>([]);
  readonly carregandoLista = signal(false);
  readonly carregandoMais = signal(false);
  readonly erroLista = signal(false);
  private temProximaPagina = false;
  private pagina = 0;
  private readonly pedidos$ = new Subject<PedidoPaginaTemplates>();
  private readonly digitacao$ = new Subject<void>();

  busca = signal('');
  selecionadoId = signal<number | null>(null);
  detalhe = signal<TemplateBusca | null>(null);
  carregandoDetalhe = signal(false);
  erroDetalhe = signal(false);
  variaveis = signal<VariavelSelecao[]>([]);
  enviando = signal(false);

  private envio: EnvioMensagemTemplate | null = null;
  private detalheSubscription?: Subscription;
  private preCarregamentoTimer?: ReturnType<typeof setTimeout>;

  readonly selecionado = computed(() => this.templates().find(t => t.id === this.selecionadoId()) ?? null);

  readonly segmentosPrevia = computed<SegmentoPrevia[]>(() => {
    const corpo = this.componente('BODY')?.text ?? '';
    const valores = new Map(this.variaveis().map(v => [v.ordem, v.valor.trim()]));
    return corpo.split(/(\{\{\d+\}\})/g).filter(Boolean).map(parte => {
      const ordem = parte.match(/^\{\{(\d+)\}\}$/)?.[1];
      if (!ordem) return { texto: parte, variavel: false };
      return { texto: valores.get(Number(ordem)) || parte, variavel: true };
    });
  });

  readonly cabecalhoTexto = computed(() => {
    const header = this.componente('HEADER');
    return header?.format === 'IMAGE' ? '' : (header?.text ?? '');
  });

  readonly cabecalhoImagem = computed(() =>
    this.componente('HEADER')?.format === 'IMAGE' && this.detalhe() ? (this.envio?.urlImagem ?? '') : ''
  );

  readonly rodapeTexto = computed(() => this.componente('FOOTER')?.text ?? '');

  readonly botoes = computed(() =>
    (this.componente('BUTTONS')?.buttons ?? []).map(b => b?.text ?? '').filter(Boolean)
  );

  readonly variaveisFixas = computed(() => this.variaveis().filter(v => v.campoFixo));
  readonly variaveisDigitadas = computed(() => this.variaveis().filter(v => !v.campoFixo));
  readonly podeEnviar = computed(() =>
    !!this.detalhe() && !this.carregandoDetalhe() && !this.enviando() && this.variaveis().every(v => v.valor.trim())
  );

  private autoSelecionou = false;
  private readonly autoSelecao = effect(() => {
    const primeiro = this.templates()[0];
    if (this.autoSelecionou || !primeiro) return;
    this.autoSelecionou = true;
    if (this.elementRef.nativeElement.clientWidth >= LARGURA_MINIMA_DUAS_COLUNAS) {
      untracked(() => this.selecionar(primeiro));
    }
  }, { allowSignalWrites: true });

  ngOnInit(): void {
    this.pedidos$
      .pipe(
        switchMap(pedido => this.templatesChat.buscarPagina(pedido.busca, pedido.pagina).pipe(
          map(resultado => ({ pedido, resultado: resultado as TemplatesChatPagina | null })),
          catchError(() => of({ pedido, resultado: null }))
        )),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ pedido, resultado }) => this.aplicarPagina(pedido, resultado));

    this.digitacao$
      .pipe(debounceTime(ESPERA_BUSCA_TEMPLATES_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.recarregarLista());

    const emCache = this.templatesChat.primeiraPaginaEmCache();
    if (emCache) {
      this.aplicarPagina({ busca: '', pagina: 0 }, emCache);
    } else {
      this.recarregarLista();
    }
  }

  aoDigitarBusca(valor: string): void {
    this.busca.set(valor);
    this.digitacao$.next();
  }

  aoRolarLista(): void {
    this.verificarPreCarga();
  }

  private recarregarLista(): void {
    this.pagina = 0;
    this.temProximaPagina = false;
    this.erroLista.set(false);
    this.carregandoMais.set(false);
    this.carregandoLista.set(true);
    this.pedidos$.next({ busca: this.busca(), pagina: 0 });
  }

  private verificarPreCarga(): void {
    const lista = this.listaTemplates?.nativeElement;
    if (!lista || !this.temProximaPagina || this.carregandoLista() || this.carregandoMais()) return;
    if (lista.scrollHeight - lista.scrollTop - lista.clientHeight > lista.clientHeight * TELAS_PRE_CARGA_TEMPLATES) return;
    this.carregandoMais.set(true);
    this.pedidos$.next({ busca: this.busca(), pagina: this.pagina + 1 });
  }

  private aplicarPagina(pedido: PedidoPaginaTemplates, resultado: TemplatesChatPagina | null): void {
    this.carregandoLista.set(false);
    this.carregandoMais.set(false);
    if (!resultado) {
      if (pedido.pagina === 0) this.erroLista.set(true);
      return;
    }
    const novos = resultado.templates ?? [];
    if (pedido.pagina === 0) {
      this.templates.set(novos);
    } else {
      const ids = new Set(this.templates().map(t => t.id));
      this.templates.set([...this.templates(), ...novos.filter(t => !ids.has(t.id))]);
    }
    this.pagina = pedido.pagina;
    this.temProximaPagina = !!resultado.temProximaPagina;
    setTimeout(() => this.verificarPreCarga(), 0);
  }

  ngOnDestroy(): void {
    this.detalheSubscription?.unsubscribe();
    clearTimeout(this.preCarregamentoTimer);
  }

  @HostListener('document:keydown.escape')
  aoPressionarEsc(): void {
    if (!this.enviando()) this.fechar.emit();
  }

  fecharPeloFundo(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.enviando()) this.fechar.emit();
  }

  tentarNovamenteLista(): void {
    this.recarregarLista();
  }

  selecionar(template: TemplateChatResumo): void {
    if (this.enviando() || this.selecionadoId() === template.id) return;
    this.selecionadoId.set(template.id);
    this.carregarDetalhe(template.id);
  }

  alternar(template: TemplateChatResumo): void {
    if (this.enviando()) return;
    if (this.selecionadoId() === template.id) {
      this.limparSelecao();
      return;
    }
    this.selecionar(template);
  }

  tentarNovamenteDetalhe(): void {
    const id = this.selecionadoId();
    if (id != null) this.carregarDetalhe(id);
  }

  preCarregar(template: TemplateChatResumo): void {
    clearTimeout(this.preCarregamentoTimer);
    this.preCarregamentoTimer = setTimeout(() => this.templatesChat.preCarregar(template.id), 150);
  }

  cancelarPreCarregamento(): void {
    clearTimeout(this.preCarregamentoTimer);
  }

  alterarVariavel(ordem: number, valor: string): void {
    this.variaveis.update(lista => lista.map(v => v.ordem === ordem ? { ...v, valor } : v));
  }

  rotuloVariavel(ordem: number): string {
    return '{{' + ordem + '}}';
  }

  async enviar(): Promise<void> {
    if (!this.envio || !this.podeEnviar()) {
      if (this.variaveis().some(v => !v.valor.trim())) this.msgService.msgAdvertencia('Preencha as variáveis do template.');
      return;
    }
    const valores = new Map(this.variaveis().map(v => [v.ordem, v.valor.trim()]));
    this.envio.variaveis = this.envio.variaveis.map(v => ({ ...v, valor: valores.get(v.ordem) ?? '' }));

    this.enviando.set(true);
    try {
      if (await this.envioTemplateChatService.preparar(this.envio)) {
        this.enviado.emit(this.envio);
      }
    } finally {
      this.enviando.set(false);
    }
  }

  private carregarDetalhe(templateId: number): void {
    this.detalheSubscription?.unsubscribe();
    clearTimeout(this.preCarregamentoTimer);
    this.detalhe.set(null);
    this.variaveis.set([]);
    this.envio = null;
    this.erroDetalhe.set(false);
    this.carregandoDetalhe.set(true);

    this.detalheSubscription = this.templatesChat.detalhe(templateId).subscribe({
      next: template => {
        if (this.selecionadoId() !== templateId) return;
        this.envio = this.envioTemplateChatService.montarEnvio(template, this.numeroContato, this.nomeContato);
        this.envio.fluxoId = template.conta?.fluxo?.id;
        this.variaveis.set(this.montarVariaveis(template));
        this.detalhe.set(template);
        this.carregandoDetalhe.set(false);
      },
      error: () => {
        if (this.selecionadoId() !== templateId) return;
        this.erroDetalhe.set(true);
        this.carregandoDetalhe.set(false);
      }
    });
  }

  private limparSelecao(): void {
    this.detalheSubscription?.unsubscribe();
    this.selecionadoId.set(null);
    this.detalhe.set(null);
    this.variaveis.set([]);
    this.envio = null;
    this.carregandoDetalhe.set(false);
    this.erroDetalhe.set(false);
  }

  private montarVariaveis(template: TemplateBusca): VariavelSelecao[] {
    const exemplos = template.components?.find(c => c.type === 'BODY')?.example ?? [];
    return exemplos
      .filter(e => e.ordem != null)
      .map(e => {
        const fixo = this.valorCampoFixo(e.campoFixo);
        return {
          ordem: e.ordem as number,
          valor: fixo ?? '',
          exemplo: e.valor ?? '',
          campoFixo: fixo ? ROTULO_CAMPO_FIXO[e.campoFixo!] : '',
        };
      });
  }

  private valorCampoFixo(campoFixo?: string | null): string | null {
    switch (campoFixo) {
      case 'CONTATO_NOME': return this.nomeContato?.trim() || null;
      case 'CONTATO_NUMERO': return this.numeroContato?.trim() || null;
      case 'OPERADOR_NOME': return this.authService.getUsuarioLogadoClariBot()?.nome?.trim() || null;
      default: return null;
    }
  }

  private componente(tipo: string) {
    return this.detalhe()?.components?.find(c => c.type === tipo);
  }
}
